import React, { useState, useRef, useEffect, useCallback } from "react";
import Navigation from "../partials/Navigation";
import { Helmet } from "react-helmet";
import HorizontalNav from "../partials/Dashboard/HorizontalNav";
import TableContainer from "../partials/TableContainer";
import TaskTable from "../partials/TaskTable";
import jwt_decode from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import { Button, Spinner } from "react-bootstrap";
import { getExpiry } from "../common";
import { getTasksFromAPI } from "../redux/actions/my-tasks";
import ActionPlan from "../partials/Forms/ActionPlan";
import Implementation from "../partials/Forms/Implementation";
import { useTranslation } from "react-i18next";
import { getBoardById } from "../redux/actions/boards";
import { SET_SINGLE_BOARD_FROM_STORE } from "../redux/types";
import ConfirmationBox from "../partials/ConfirmationBox";
import { deleteTask } from "../redux/actions/tasks";
import configVars from "../config";
import { useParams } from "react-router-dom";

const MyTasks = (props) => {
  const params = useParams();
  const [translation] = useTranslation("translations");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const myTasks = useSelector((state) => state.myTasks);
  const [columnsData, setColumnsData] = useState({});
  const dispatch = useDispatch();
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState();
  const [showActionPlanUpdate, setShowActionPlanUpdate] = useState(false);
  const [config, setConfig] = useState(null);
  const stopLoading = useRef(false);
  const [showImplementationUpdate, setShowImplementationUpdate] =
    useState(false);
  const board = useSelector((state) => state.boards.singleBoard);
  const allBoardsDetails = useSelector(
    (state) => state.boards.allBoardsDetails
  );
  const [cookies] = useCookies([
    "sustainyou_user_jwt",
    "sustainyou_jwt_issued",
    "sustainyou_jwt_expire",
  ]);

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };
  const handleShowConfirmation = () => setShowConfirmation(true);

  const getConfigs = async (userJwt) => {
    setConfigLoading(true);
    try {
      let response = await fetch(
        `${configVars.apiUrl}sustainchange/v1/getconfigurations/?token=${userJwt}`,
        {
          method: "GET",
          headers: {
            Authorization: `Basic ${configVars.auth}`,
            "Content-Type": "application/json",
          },
        }
      );
      const _configs = await response.json();
      setConfig(_configs?.data);
    } catch (error) {
      console.log({ error });
    }
    setConfigLoading(false);
  };

  const getSingleBoard = useCallback(
    async (id, userJwt) => {
      try {
        setIsLoading(true);
        await dispatch(getBoardById(id, userJwt));
        setIsLoading(false);
      } catch (error) {
        console.log({ error });
        setIsLoading(false);
      }
    },
    [dispatch]
  );
  async function _refreshUserTasks() {
    // const jwtDecoded = jwt_decode(cookies.sustainyou_user_jwt);
    // const userId = jwtDecoded?.id;
    if (cookies.sustainyou_user_jwt) {
      try {
        setIsLoading(true);
        stopLoading.current = false;
        await dispatch(getTasksFromAPI(cookies.sustainyou_user_jwt));
        setIsLoading(false);
      } catch (error) {
        console.log({ error });
        setIsLoading(false);
      }
    }
  }
  const setSingleBoardOnTaskSelect = useCallback(
    (boardId) => {
      let expired = getExpiry(`sustainchange_single_board_expiry_${boardId}`);
      if (cookies.sustainyou_user_jwt) {
        const jwtDecoded = jwt_decode(cookies.sustainyou_user_jwt);
        if (cookies.sustainyou_user_jwt && config == null) {
          getConfigs(cookies.sustainyou_user_jwt);
        }
        if (jwtDecoded && (!allBoardsDetails[boardId] || expired)) {
          getSingleBoard(boardId, jwtDecoded.id);
        } else if (allBoardsDetails[boardId]) {
          dispatch({
            type: SET_SINGLE_BOARD_FROM_STORE,
            payload: allBoardsDetails[boardId],
          });
        }
      }
    },
    [cookies, config, allBoardsDetails, getSingleBoard, dispatch]
  );

  useEffect(() => {
    if (!stopLoading.current) {
      const _checkForUrlTaskId = () => {
        if (params?.task_id) {
          const taskId = parseInt(params?.task_id, 10);
          let found = false;
          myTasks.actionPlans.every((task) => {
            if (task?.task?.id === taskId) {
              setSelectedTask(task);
              setSingleBoardOnTaskSelect(task?.board_id);
              setShowActionPlanUpdate(true);
              found = true;
              return false;
            }
            return true;
          });
          if (!found) {
            myTasks.implementations.every((task) => {
              if (task?.task?.id === taskId) {
                setSelectedTask(task);
                setSingleBoardOnTaskSelect(task?.board_id);
                setShowImplementationUpdate(true);
                found = true;
                return false;
              }
              return true;
            });
          }
        }
      };
      let expired = getExpiry("sustainchange_my_tasks_expiry");

      const fetchMyTasksData = async (userJwt) => {
        setIsLoading(true);
        await dispatch(getTasksFromAPI(userJwt));
        _checkForUrlTaskId();
        setIsLoading(false);
      };
      if (cookies.sustainyou_user_jwt) {
        // const jwtDecoded = jwt_decode(cookies.sustainyou_user_jwt);
        if (cookies.sustainyou_user_jwt && expired) {
          fetchMyTasksData(cookies.sustainyou_user_jwt);
        } else {
          _checkForUrlTaskId();
        }
      }
      stopLoading.current = true;
    }
  }, [cookies, dispatch, myTasks, params, setSingleBoardOnTaskSelect]);

  const _showUpdateForm = (task, type) => {
    props.history.replace({
      pathname: `/my-tasks/${task?.task?.id}`,
    });
    setSelectedTask(task);
    setSingleBoardOnTaskSelect(task?.board_id);
    if (type === "ActionPlan") {
      setShowActionPlanUpdate(true);
    } else {
      setShowImplementationUpdate(true);
    }
  };

  useEffect(() => {
    if (board) {
      setColumnsData({ ...board });
    }
  }, [board]);

  const _handleClose = () => {
    setShowActionPlanUpdate(false);
    setShowImplementationUpdate(false);
    props.history.replace({
      pathname: `/my-tasks`,
    });
  };

  const handleDeleteTask = async () => {
    try {
      setConfirmationLoading(true);

      await dispatch(
        deleteTask(
          selectedTask?.task?.id,
          selectedTask?.task?.tasklist_id,
          selectedTask?.task?.post_status,
          selectedTask?.task
        )
      );
      setShowConfirmation(false);
      _handleClose();

      setConfirmationLoading(false);
    } catch (error) {
      console.log({ error });
      setConfirmationLoading(false);
    }

    handleCloseConfirmation();
  };
  const _handleDelete = () => {
    handleShowConfirmation();
  };

  const getColumnNo = (id) => {
    let _index = columnsData?.columnOrder?.findIndex(
      (item) => parseInt(item.id) === parseInt(id)
    );
    let columnName = null;
    if (_index > -1) {
      columnName = columnsData.columnOrder[_index].tasklist_title;
    }
    return { columnNo: _index, columnName: columnName };
  };

  const _renderTable = (type) => {
    if ((isLoading || configLoading) && !stopLoading.current) {
      return (
        <div className="d-flex align-items-center justify-content-center mt-5">
          <Spinner animation="border" size="lg" variant="primary" />
        </div>
      );
    }
    const data =
      type === "ActionPlan" ? myTasks.actionPlans : myTasks.implementations;
    if (data == null || data?.length === 0) {
      return (
        <div className="d-flex align-items-center justify-content-center mt-5">
          <p>No records found!</p>
        </div>
      );
    }

    return (
      <TaskTable
        tasks={data}
        type={type}
        onClickTableItem={(task) => _showUpdateForm(task, type)}
      />
    );
  };

  return (
    <div className="main-wrapper">
      <ConfirmationBox
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        title={translation("delete_task")}
        message={`${translation("delete_warning")} ${selectedTask?.title}?`}
        confirmationAction={handleDeleteTask}
        loading={confirmationLoading}
      />
      <ActionPlan
        config={config}
        show={showActionPlanUpdate && !isLoading}
        selectedTask={selectedTask?.task}
        handleClose={_handleClose}
        board={board}
        columnId={selectedTask?.task?.tasklist_id}
        selectedColumn={selectedTask?.task?.tasklist_id}
        handleDelete={_handleDelete}
        getColumnNo={getColumnNo}
        columnTitle="Action Plan"
        showArchiveButtons={false}
        setShowCloseConfirmation={() => {}}
      />
      <Implementation
        config={config}
        show={showImplementationUpdate && !isLoading}
        selectedTask={selectedTask?.task}
        handleClose={_handleClose}
        board={board}
        columnId={selectedTask?.task?.tasklist_id}
        selectedColumn={selectedTask?.task?.tasklist_id}
        handleDelete={_handleDelete}
        getColumnNo={getColumnNo}
        columnTitle="Implementation"
        showArchiveButtons={false}
        setShowCloseConfirmation={() => {}}
      />

      {window.location.pathname === "/" &&
        <TableContainer
          heading={translation("implementation-heading")}
          className="implementation"
        >
          {_renderTable("Implementation")}
        </TableContainer>
      }

      {window.location.pathname !== "/" &&
        <>
          <Helmet>
            <title>{translation("my-tasks")}</title>
            <meta name="description" content="" />
          </Helmet>

          <Navigation />

          <div className="page-content-wrap">
            <HorizontalNav title={translation("my-tasks")} />
            <div className="pt-3 page-content-inner my-tasks-container">
              <Button
                variant="downriver"
                onClick={_refreshUserTasks}
                className="text-white rounded my-task-refresh"
                size="sm"
              >
                {translation("refresh")}
              </Button>
              <TableContainer
                heading={translation("implementation-heading")}
                className="implementation"
              >
                {_renderTable("Implementation")}
              </TableContainer>
              <TableContainer
                heading={translation("actionplan-heading")}
                className="action-plan"
              >
                {_renderTable("ActionPlan")}
              </TableContainer>
            </div>
          </div>
        </>
      }
      
    </div>
  );
};

export default MyTasks;
